import { bffUrl } from "@/shared/api/config";
import { decodeJwtPayload } from "@/shared/api/jwt";
import { readLocaleCookie } from "@/shared/i18n/locale-cookie";
import { accountTheme, setTheme } from "@/shared/ui/theme";

export interface BffSession {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  userId: string;
  userName: string;
}

export type SessionStatus = "loading" | "authenticated" | "unauthenticated";

export interface SessionSnapshot {
  status: SessionStatus;
  session: BffSession | null;
}

export type SignInOutcome =
  | { ok: true }
  | { ok: false; reason: "invalid_credentials" | "unreachable" }
  | { ok: false; reason: "two_factor_required"; status: TwoFactorStatus };

type TwoFactorStatus = "TFAL" | "TFAC";

export type SignInCodeOutcome =
  | { ok: true }
  | { ok: false; reason: "invalid_code" | "expired" | "unreachable" };

const STORAGE_KEY = "simon.bff-session";
const REFRESH_SKEW_MS = 60_000;
const RETRY_AFTER_FAILURE_MS = 30_000;
const SIGN_IN_PATH = "/auth/signin";

const LOADING_SNAPSHOT: SessionSnapshot = { status: "loading", session: null };

let snapshot: SessionSnapshot = LOADING_SNAPSHOT;
let booted = false;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let refreshInFlight: Promise<boolean> | null = null;
let sessionRevision = 0;
let unauthenticatedRedirect = SIGN_IN_PATH;
let pendingTwoFactor: { token: TokenResponse; userName: string; expiresAt: number } | null = null;
const listeners = new Set<() => void>();

export function bootSession(): void {
  if (booted) return;
  booted = true;
  window.addEventListener("storage", onStorage);
  document.addEventListener("visibilitychange", onVisible);
  const stored = readStored();
  if (!stored) {
    adopt(null, false);
    return;
  }
  if (isFresh(stored)) {
    adopt(stored, false);
    return;
  }
  snapshot = { status: "loading", session: stored };
  emit();
  void refreshSession();
}

export function subscribeSession(listener: () => void): () => void {
  listeners.add(listener);
  bootSession();
  return () => {
    listeners.delete(listener);
  };
}

export function getSessionSnapshot(): SessionSnapshot {
  return snapshot;
}

export function currentSession(): BffSession | null {
  return snapshot.session;
}

export async function signIn(userName: string, password: string): Promise<SignInOutcome> {
  bootSession();
  let res: Response;
  try {
    res = await postJson("v1/auth/login-password", {
      user_name: userName,
      user_password: password,
    });
  } catch {
    return { ok: false, reason: "unreachable" };
  }
  if (res.status === 401 || res.status === 403 || res.status === 400) {
    return { ok: false, reason: "invalid_credentials" };
  }
  if (!res.ok) return { ok: false, reason: "unreachable" };

  let token: TokenResponse;
  try {
    token = (await res.json()) as TokenResponse;
  } catch {
    return { ok: false, reason: "unreachable" };
  }
  if (token.status !== "FULL") {
    pendingTwoFactor = {
      token,
      userName,
      expiresAt: Date.now() + (token.expires_in ?? 0) * 1000,
    };
    return { ok: false, reason: "two_factor_required", status: token.status };
  }

  pendingTwoFactor = null;
  const session = toSession(token, userName, "");
  await applyAccountTheme(session);
  adopt(session);
  return { ok: true };
}

export async function signInWithCode(code: string): Promise<SignInCodeOutcome> {
  const pending = pendingTwoFactor;
  if (!pending) return { ok: false, reason: "expired" };
  if (Date.now() >= pending.expiresAt) {
    pendingTwoFactor = null;
    return { ok: false, reason: "expired" };
  }
  let res: Response;
  try {
    res = await postJson(
      "v1/auth/login-2fa",
      { user_name: pending.userName, code },
      pending.token.access_token,
    );
  } catch {
    return { ok: false, reason: "unreachable" };
  }
  if (res.status === 400 || res.status === 401 || res.status === 403) {
    return { ok: false, reason: "invalid_code" };
  }
  if (!res.ok) return { ok: false, reason: "unreachable" };

  let token: TokenResponse;
  try {
    token = (await res.json()) as TokenResponse;
  } catch {
    return { ok: false, reason: "unreachable" };
  }
  if (token.status !== "FULL") return { ok: false, reason: "invalid_code" };

  pendingTwoFactor = null;
  const session = toSession(token, pending.userName, "");
  await applyAccountTheme(session);
  adopt(session);
  return { ok: true };
}

export function cancelTwoFactor(): void {
  pendingTwoFactor = null;
}

export function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  const s = snapshot.session ?? readStored();
  if (!s) {
    adopt(null, false);
    return Promise.resolve(false);
  }
  const revision = sessionRevision;
  const isStale = (): boolean => sessionRevision !== revision;
  refreshInFlight = (async (): Promise<boolean> => {
    try {
      const res = await postJson("v1/auth/refresh-login", { refresh_token: s.refreshToken });
      if (isStale()) return snapshot.status === "authenticated";
      if (res.ok) {
        const tokenResponse = (await res.json()) as TokenResponse;
        if (isStale()) return snapshot.status === "authenticated";
        adopt(toSession(tokenResponse, s.userName, s.userId));
        return true;
      }
      if (res.status === 401 || res.status === 403) {
        const other = readStored();
        if (other && other.accessToken !== s.accessToken && isFresh(other)) {
          adopt(other, false);
          return true;
        }
        adopt(null);
        return false;
      }
      scheduleRetry();
      return snapshot.status === "authenticated";
    } catch {
      if (!isStale()) scheduleRetry();
      return snapshot.status === "authenticated";
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

export async function getAccessToken(): Promise<string | null> {
  bootSession();
  if (refreshInFlight) await refreshInFlight;
  const s = snapshot.session;
  if (!s) return null;
  if (isFresh(s)) return s.accessToken;
  const ok = await refreshSession();
  return ok ? (snapshot.session?.accessToken ?? null) : null;
}

export function revokeSession(): void {
  adopt(null);
}

export async function signOut(opts?: { callbackUrl?: string }): Promise<void> {
  const callbackUrl = opts?.callbackUrl ?? SIGN_IN_PATH;
  const s = snapshot.session;
  unauthenticatedRedirect = callbackUrl;
  adopt(null);
  if (s) {
    try {
      await postJson("v1/auth/logout", {}, s.accessToken);
    } catch {}
  }
  if (window.location.href !== new URL(callbackUrl, window.location.href).href) {
    window.location.assign(callbackUrl);
  }
}

export function getUnauthenticatedRedirect(): string {
  return unauthenticatedRedirect;
}

function readStored(): BffSession | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object") return null;
    const s = parsed as Partial<BffSession>;
    if (
      typeof s.accessToken !== "string" ||
      typeof s.refreshToken !== "string" ||
      typeof s.expiresAt !== "number" ||
      typeof s.userId !== "string" ||
      typeof s.userName !== "string"
    ) {
      return null;
    }
    return {
      accessToken: s.accessToken,
      refreshToken: s.refreshToken,
      expiresAt: s.expiresAt,
      userId: s.userId,
      userName: s.userName,
    };
  } catch {
    return null;
  }
}

function writeStored(session: BffSession | null): void {
  try {
    if (session) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {}
}

function isFresh(session: BffSession): boolean {
  return session.expiresAt - Date.now() > REFRESH_SKEW_MS;
}

function adopt(session: BffSession | null, persist = true): void {
  clearTimer();
  sessionRevision += 1;
  snapshot = {
    status: session ? "authenticated" : "unauthenticated",
    session,
  };
  if (persist) writeStored(session);
  if (session) {
    unauthenticatedRedirect = SIGN_IN_PATH;
    scheduleRefresh(session);
  }
  emit();
}

function emit(): void {
  for (const listener of listeners) listener();
}

function clearTimer(): void {
  if (refreshTimer !== null) clearTimeout(refreshTimer);
  refreshTimer = null;
}

function scheduleRefresh(session: BffSession): void {
  clearTimer();
  const delay = Math.max(5_000, session.expiresAt - Date.now() - REFRESH_SKEW_MS);
  refreshTimer = setTimeout(() => void refreshSession(), delay);
}

function scheduleRetry(): void {
  clearTimer();
  refreshTimer = setTimeout(() => void refreshSession(), RETRY_AFTER_FAILURE_MS);
}

function toSession(
  tokenResponse: TokenResponse,
  userName: string,
  fallbackUserId: string,
): BffSession {
  return {
    accessToken: tokenResponse.access_token,
    refreshToken: tokenResponse.refresh_token,
    expiresAt: Date.now() + (tokenResponse.expires_in ?? 0) * 1000,
    userId: decodeJwtPayload(tokenResponse.access_token)?.sub ?? fallbackUserId,
    userName,
  };
}

async function applyAccountTheme(session: BffSession): Promise<void> {
  if (!session.userId) return;
  try {
    const res = await fetch(bffUrl(`v1/user/${encodeURIComponent(session.userId)}`), {
      headers: { Accept: "application/json", Authorization: `Bearer ${session.accessToken}` },
      cache: "no-store",
    });
    if (!res.ok) return;
    const user = (await res.json()) as { setting?: { theme_type?: string | null } | null };
    const themeType = user.setting?.theme_type;
    if (themeType) setTheme(accountTheme(themeType));
  } catch {}
}

async function postJson(path: string, body: unknown, accessToken?: string): Promise<Response> {
  const headers: Record<string, string> = {
    "Accept-Language": readLocaleCookie() ?? "sv",
    "Content-Type": "application/json",
  };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  return fetch(bffUrl(path), {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    cache: "no-store",
  });
}

function onStorage(event: StorageEvent): void {
  if (event.key !== STORAGE_KEY) return;
  const other = readStored();
  const currentSession = snapshot.session;
  if (other?.accessToken === currentSession?.accessToken) return;
  adopt(other, false);
}

function onVisible(): void {
  if (document.visibilityState !== "visible") return;
  const currentSession = snapshot.session;
  if (currentSession && !isFresh(currentSession)) void refreshSession();
}

interface TokenResponse {
  status: "TFAC" | "TFAL" | "FULL";
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token: string;
}
